import React, {useState} from 'react';
import {useForm} from "react-hook-form";
import {joiResolver} from "@hookform/resolvers/joi";
import {Link, useNavigate} from "react-router-dom";
import {jwtDecode} from "jwt-decode";
import {useDispatch} from "react-redux";

import styles from './LoginPage.module.scss';
import logo from '../../constants/images/logo.svg';
import CustomCheckbox from "../../components/UI/CustomCheckbox/CustomCheckbox";
import {MAIN_ROUTES} from "../../routing/main_routes";
import {linkStyles} from "../../constants/linkStyles";
import {loginValidator} from "../../validators/login.validator";
import {userService} from "../../services/user.service";
import {verificationService} from "../../services/verification.service";
import {getHttpErrorMessageByStatus} from "../../constants/getHttpErrorMessageByStatus";
import {userDetailsActions} from "../../store/slices/userDetailsSlice";
import Loader from "../../components/Loader/Loader";
import {cleanerDetailsActions} from "../../store/slices/cleanerDetailsSlice";
import {cleaningRequestsActions} from "../../store/slices/cleaningRequestsSlice";
import MainButtonTransparentBackground
    from "../../components/UI/MainButtonTransparentBackground/MainButtonTransparentBackground";


const LoginPage = ({setIsCleaner, setIsRemembered, isRemembered}) => {
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
    } = useForm({
        resolver: joiResolver(loginValidator),
        mode: 'all',
    });

    const onSubmit = async (data) => {
        await localStorage.clear();
        sessionStorage.setItem('isRemembered', JSON.stringify(isRemembered));
        localStorage.setItem('isRemembered', JSON.stringify(isRemembered));

        try {
            setIsLoading(true);

            const {data: tokens} = await userService.login(data);
            const {is_blocked, cleaner_id, user_id, iat, exp} = jwtDecode(tokens.access);

            // Only banned accounts are blocked from logging in. A cleaner whose
            // verification is still pending CAN log in and browse; bidding is
            // gated separately on the server (and surfaced when they try).
            if (is_blocked) {
                setError("Ваш акаунт заблоковано. Зверніться до підтримки.");
                return;
            }

            // Persist the tokens BEFORE any authenticated request. The axios
            // interceptor reads the access token from localStorage; if we
            // dispatch getById / current-user-requests first they fire without
            // an Authorization header and 401 (localStorage.clear() above wiped
            // any previous token).
            localStorage.setItem('token', JSON.stringify(tokens));
            localStorage.setItem('expires', JSON.stringify({iat, exp}));

            if (cleaner_id) {
                await dispatch(cleanerDetailsActions.getById({id: cleaner_id}));
                localStorage.setItem('isCleaner', 'true');
                setIsCleaner(true);
                // Surface the re-verification prompt right at login (the admin may
                // have requested additional / re-uploaded documents). We don't
                // hard-block login — the cleaner can still browse — but we deep-link
                // them to the verification flow, where AllCleaningRequests opens the
                // PARTIAL ModalReVerification listing the requested document types.
                let needsReverification = false;
                try {
                    const {data: vstatus} = await verificationService.getStatus();
                    needsReverification = Boolean(vstatus?.needs_reverification);
                } catch (statusErr) {
                    console.error(statusErr);
                }
                navigate(
                    needsReverification
                        ? `${MAIN_ROUTES.ALL_CLEANING_REQUESTS}?reverify=1`
                        : MAIN_ROUTES.ALL_CLEANING_REQUESTS
                );
            } else {
                await dispatch(userDetailsActions.getById({id: user_id}));
                await dispatch(cleaningRequestsActions.getActiveByCurrentUser());
                navigate(MAIN_ROUTES.MAIN, {state: {id: user_id}});
                localStorage.setItem('isCleaner', 'false');
                setIsCleaner(false);
            }
        } catch (error) {
            const errorMessage = getHttpErrorMessageByStatus(error);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }

    const handleClearError = () => {
        if (error) {
            setError(null);
        }
    }

    return (
        isLoading ?
            <Loader/> :
            <div className={styles.loginPage_wrapper}>
                <div className={styles.loginPage_wrapper_login}>
                    <div className={styles.loginPage_wrapper_login_title}>
                        <img src={logo} alt="quality cleaning logo"/>

                        <div>
                            <h1 className={styles.title}>Увійти в Аккаунт</h1>
                            <p className={styles.paragraph}>З поверненням! Будь ласка, введіть свої дані.</p>
                        </div>
                    </div>

                    <form
                        onChange={handleClearError}
                        onSubmit={handleSubmit(onSubmit)}
                        action="#"
                        className={styles.loginPage_wrapper_login_form}>
                        <div className={styles.loginPage_wrapper_login_form_block}>
                            <label>
                                Email:
                                <input
                                    {...register('email')}
                                    className={styles.input}
                                    type="email"
                                    placeholder={'Введіть ваш email'}
                                    autoComplete='new_email'
                                />
                                {errors.email && <span className={styles.error}> {errors.email.message} </span>}
                            </label>
                        </div>

                        <div className={styles.loginPage_wrapper_login_form_block}>
                            <label>
                                Пароль
                                <input
                                    {...register('password')}
                                    className={styles.input}
                                    type="password"
                                    autoComplete='new_password'
                                />
                            </label>
                            {errors.password && <span className={styles.error}> {errors.password.message} </span>}
                            {error && <span className={styles.error}> {error} </span>}
                        </div>

                        <div className={styles.loginPage_wrapper_login_form_rememberMe}>
                            <CustomCheckbox
                                text={'Запам’ятати'}
                                id={null}
                                isChecked={isRemembered}
                                handleCheckboxChange={() => setIsRemembered(prev => !prev)}
                            />

                            <p className={styles.forgotPassword}>
                                <Link to={MAIN_ROUTES.FORGOT_PASSWORD_STEP_1} style={linkStyles}>Забули пароль</Link>
                            </p>
                        </div>

                        <MainButtonTransparentBackground
                            type={'submit'}
                            onClick={null}
                            text={'Увійти'}
                            condition={!isValid}
                            width={'100%'}
                        />
                    </form>

                    <div className={styles.paragraph}>
                        <p>Не маєте аккаунту?</p>
                        <div style={{display: 'flex', gap: '4px'}}>
                            <Link onClick={() => {
                                localStorage.setItem("currentStep", JSON.stringify(1))
                            }} to={MAIN_ROUTES.CREATE_ORDER} style={linkStyles}> Зробіть замовлення</Link>
                            <p>або</p>
                            <Link to={MAIN_ROUTES.REGISTRATION_CLEANER} style={linkStyles}> Cтаньте клінером</Link>
                        </div>
                    </div>
                </div>
            </div>
    );
};

export default LoginPage;
